import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Header from "../components/Header";
import FeedbackDialog from "../components/FeedbackDialog";
import { api, apiErr } from "../lib/api";
import { Button } from "../components/ui/button";
import Markdown from "../components/Markdown";
import { CheckCircle2, XCircle, ArrowLeft, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";

export default function MockTest() {
  const { cls, subject } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ans, setAns] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const load = () => {
    setLoading(true); setAns({}); setSubmitted(false);
    api.get(`/mock-test/${cls}/${subject}`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(apiErr(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [cls, subject]);

  const score = data ? Object.entries(ans).reduce((s, [i, a]) => s + (a === data.questions[i].answer_index ? 1 : 0), 0) : 0;

  return (
    <div className="min-h-screen">
      <Header />
      <FeedbackDialog />
      <div className="max-w-4xl mx-auto px-4 sm:px-8 py-6">
        <Button onClick={() => nav("/dashboard")} className="brut-btn rounded-none bg-white hover:bg-white text-black mb-4">
          <ArrowLeft size={16} className="mr-1" strokeWidth={2.5}/> Back
        </Button>
        <div className="brut-card p-6 bg-[#FFB3BA] mb-6 flex items-center justify-between">
          <div>
            <div className="text-xs font-bold tracking-[0.2em] uppercase">Mock Test</div>
            <h1 className="text-3xl font-black">Class {cls} • {subject === "physics" ? "Physics" : "Chemistry"}</h1>
          </div>
          <Button onClick={load} data-testid="new-mock-btn" className="brut-btn rounded-none bg-white hover:bg-white text-black">
            <RefreshCw size={14} className="mr-1" strokeWidth={2.5}/> New Test
          </Button>
        </div>

        {loading ? (
          <div className="brut-card p-8 bg-white flex items-center gap-3">
            <Loader2 className="animate-spin" strokeWidth={2.5}/>
            <span className="font-bold">Preparing your mock test…</span>
          </div>
        ) : data ? (
          <div className="brut-card p-6 bg-white" data-testid="mock-content">
            {submitted && (
              <div className="brut-card p-4 bg-[#E8F3EC] mb-6" data-testid="mock-score">
                <div className="text-2xl font-black">Score: {score} / {data.questions.length}</div>
              </div>
            )}
            <div className="space-y-6">
              {data.questions.map((q, i) => (
                <div key={i} className="brut-border p-4" data-testid={`mock-q-${i}`}>
                  <div className="font-bold mb-2">
                    <span className="mr-2">{i + 1}.</span>
                    <Markdown className="inline">{q.q}</Markdown>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-2">
                    {q.options.map((opt, j) => {
                      const chosen = ans[i] === j;
                      const correct = submitted && j === q.answer_index;
                      const wrong = submitted && chosen && j !== q.answer_index;
                      return (
                        <button key={j} disabled={submitted}
                          onClick={() => setAns({ ...ans, [i]: j })}
                          data-testid={`mock-q-${i}-opt-${j}`}
                          className={`text-left brut-border brut-shadow-sm p-2 font-semibold transition-transform hover:-translate-y-0.5 disabled:hover:translate-y-0
                            ${correct ? "bg-[#E8F3EC]" : wrong ? "bg-[#FFB3BA]" : chosen ? "bg-[#FFF5D1]" : "bg-white"}`}>
                          <span className="mr-2 font-black">{String.fromCharCode(65 + j)}.</span>
                          <Markdown className="inline">{opt}</Markdown>
                          {submitted && correct && <CheckCircle2 size={16} strokeWidth={2.5} className="inline ml-2"/>}
                          {submitted && wrong && <XCircle size={16} strokeWidth={2.5} className="inline ml-2"/>}
                        </button>
                      );
                    })}
                  </div>
                  {submitted && q.explanation && (
                    <div className="mt-2 text-sm bg-[#FFF5D1] brut-border p-2">
                      <b>Explanation: </b><Markdown className="inline">{q.explanation}</Markdown>
                    </div>
                  )}
                </div>
              ))}
            </div>
            {!submitted && (
              <Button onClick={() => setSubmitted(true)} data-testid="submit-mock-btn"
                className="brut-btn rounded-none w-full mt-6 bg-[#1A1A1A] text-white hover:bg-[#1A1A1A] py-6 text-base">
                Submit Mock Test
              </Button>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}
